import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Trash2, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function RegistrationsTab() {
    const [regs, setRegs] = useState(null);
    const [teams, setTeams] = useState([]);
    const [assignTarget, setAssignTarget] = useState(null);
    const [assignForm, setAssignForm] = useState({ team_id: "", number: "" });
    const [assignSaving, setAssignSaving] = useState(false);

    const load = () => api.get("/registrations").then((r) => setRegs(r.data)).catch(() => setRegs([]));
    useEffect(() => {
        load();
        api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    }, []);

    const teamName = (id) => teams.find((t) => t.id === id)?.short_name;

    const submitAssign = async (e) => {
        e.preventDefault();
        setAssignSaving(true);
        try {
            await api.post(`/registrations/${assignTarget.id}/assign`, {
                team_id: assignForm.team_id,
                number: parseInt(assignForm.number, 10),
            });
            toast.success(`${assignTarget.first_name} assigned to roster`);
            setAssignTarget(null);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setAssignSaving(false);
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete registration for ${r.first_name} ${r.last_name}?`)) return;
        try {
            await api.delete(`/registrations/${r.id}`);
            toast.success("Registration deleted");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    if (regs === null) return <p className="text-slate-500 font-stat text-sm">Loading…</p>;

    return (
        <div data-testid="admin-registrations-tab">
            <h2 className="font-display font-bold uppercase text-xl text-white mb-6">
                Player Registrations ({regs.length})
            </h2>
            {regs.length === 0 ? (
                <div data-testid="registrations-empty" className="card-arena rounded-2xl p-12 text-center">
                    <p className="font-display font-bold uppercase text-lg text-slate-300">No registrations yet</p>
                    <p className="text-sm text-slate-500 mt-2">
                        Players who register from the homepage will appear here.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {regs.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`registration-row-${r.id}`}
                            className="card-arena rounded-2xl px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-2"
                        >
                            <div className="flex-1 min-w-[180px]">
                                <p className="font-display font-bold uppercase text-white">
                                    {r.first_name} {r.last_name}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {r.email} • {r.mobile}
                                </p>
                            </div>
                            <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FF5500]/10 text-[#FF5500] border border-[#FF5500]/40">
                                {r.position}
                            </span>
                            <span className="text-xs font-stat text-slate-400">Age {r.age}</span>
                            <span
                                className={`text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                                    r.filipino
                                        ? "bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/40"
                                        : "bg-[#1A1A28] text-slate-400 border-[#262638]"
                                }`}
                            >
                                {r.filipino ? "Filipino" : "Non-Filipino"}
                            </span>
                            {r.status === "assigned" ? (
                                <span
                                    data-testid={`assigned-chip-${r.id}`}
                                    className="text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/40"
                                >
                                    Assigned{teamName(r.assigned_team_id) ? ` → ${teamName(r.assigned_team_id)}` : ""}
                                </span>
                            ) : (
                                <button
                                    data-testid={`assign-registration-${r.id}`}
                                    onClick={() => {
                                        setAssignTarget(r);
                                        setAssignForm({ team_id: teams[0]?.id || "", number: "" });
                                    }}
                                    className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest px-3 py-2 rounded-full bg-[#FF5500]/10 border border-[#FF5500]/50 text-[#FF5500] hover:bg-[#FF5500] hover:text-white transition-colors duration-200"
                                >
                                    <UserPlus className="w-3.5 h-3.5" /> Assign
                                </button>
                            )}
                            <span className="text-xs font-stat text-slate-500">
                                {format(new Date(r.created_at), "MMM d, yyyy")}
                            </span>
                            <button
                                data-testid={`delete-registration-${r.id}`}
                                onClick={() => remove(r)}
                                className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                aria-label="Delete registration"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            Assign to Team
                        </DialogTitle>
                        <DialogDescription className="text-slate-400 text-sm">
                            {assignTarget &&
                                `${assignTarget.first_name} ${assignTarget.last_name} • ${assignTarget.position} • Age ${assignTarget.age}`}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitAssign} data-testid="assign-form" className="space-y-4 mt-2">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Team</Label>
                            <Select
                                value={assignForm.team_id}
                                onValueChange={(v) => setAssignForm({ ...assignForm, team_id: v })}
                            >
                                <SelectTrigger data-testid="assign-team" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                    <SelectValue placeholder="Select team" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                    {teams.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Jersey #</Label>
                            <Input
                                required
                                type="number"
                                min="0"
                                max="99"
                                data-testid="assign-number"
                                value={assignForm.number}
                                onChange={(e) => setAssignForm({ ...assignForm, number: e.target.value })}
                                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat"
                            />
                        </div>
                        <p className="text-xs text-slate-500">
                            Creates a roster player from this registration — add their photo afterwards via the
                            roster editor.
                        </p>
                        <button
                            type="submit"
                            data-testid="assign-submit"
                            disabled={assignSaving || !assignForm.team_id || assignForm.number === ""}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3 rounded-full transition-colors duration-200"
                        >
                            {assignSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Assign to Roster
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
